import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { messageAPI, userAPI } from '../../service/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { FiSend, FiMessageSquare, FiUser, FiArrowLeft, FiPlus, FiSearch } from 'react-icons/fi';
import PageMeta from '../../components/PageMeta';
import useAutoRefresh from '../../hooks/useAutoRefresh';

export default function MessageriePage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const { socket } = useSocket();
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showThreads, setShowThreads] = useState(true);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchThreads = async () => {
    try {
      const res = await messageAPI.getThreads();
      setThreads(res.data.data.threads);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const res = await messageAPI.getConversation(otherUserId);
      setMessages(res.data.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchThreads(); }, []);
  useAutoRefresh(() => fetchThreads(), 30000);

  useEffect(() => {
    if (!loading && userId && threads.length > 0) {
      const match = threads.find(t => t.otherUserId === parseInt(userId));
      if (match) handleSelectThread(match.otherUserId);
    }
  }, [userId, loading, threads.length]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (!socket) return;
    const handleNewMessage = (data) => {
      if (activeThread && data.expediteurId === activeThread) {
        setMessages((prev) => [...prev, {
          id: Date.now(),
          expediteurId: data.expediteurId,
          contenu: data.contenu,
          dateEnvoi: data.dateEnvoi || new Date().toISOString(),
        }]);
      }
      fetchThreads();
    };
    socket.on('new_message', handleNewMessage);
    return () => socket.off('new_message', handleNewMessage);
  }, [socket, activeThread, fetchThreads]);

  const handleSelectThread = (otherUserId) => {
    setActiveThread(otherUserId);
    fetchMessages(otherUserId);
    setShowThreads(false);
  };

  const handleBack = () => {
    setShowThreads(true);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeThread) return;
    try {
      await messageAPI.send({ destinataireId: activeThread, contenu: newMessage });
      setNewMessage('');
      fetchMessages(activeThread);
      fetchThreads();
    } catch (err) { console.error(err); }
  };

  const activeUserName = threads.find(t => t.otherUserId === activeThread)?.otherUserName || '';

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex" style={{ backgroundColor: '#FFFFFF' }}>
      <PageMeta title="Messagerie" />
      <div className={`w-full md:w-80 md:block ${showThreads ? 'block' : 'hidden'} overflow-y-auto`}
        style={{ borderRight: '1px solid #E5E5E0', flexShrink: 0 }}>
        <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E5E0' }}>
          <h2 className="font-serif font-bold text-base" style={{ color: '#0A192F' }}>Messages</h2>
          <button onClick={() => setShowNewMessage(true)} className="flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1.5 transition-colors"
            style={{ backgroundColor: '#0A192F', color: '#F4F4F0' }}>
            <FiPlus className="w-3.5 h-3.5" /> Nouveau
          </button>
        </div>
        {threads.length === 0 ? (
          <div className="p-4 text-center py-12" style={{ color: '#7A828A' }}>
            <FiMessageSquare className="w-10 h-10 mx-auto mb-3" style={{ opacity: 0.5 }} />
            <p className="text-sm">Aucune conversation</p>
          </div>
        ) : (
          threads.map((thread) => (
            <button key={thread.otherUserId} onClick={() => handleSelectThread(thread.otherUserId)}
              className={`w-full text-left p-4 transition-colors`}
              style={{ borderBottom: '1px solid #E5E5E0', backgroundColor: activeThread === thread.otherUserId && !showThreads ? '#F4F4F0' : '#FFFFFF' }}>
              <div className="flex items-center gap-3">
                {thread.otherUserAvatar ? (
                  <img src={thread.otherUserAvatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F4F4F0' }}>
                    <FiUser className="w-5 h-5" style={{ color: '#0A192F' }} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold truncate" style={{ color: '#0A192F' }}>{thread.otherUserName}</p>
                  <p className="text-xs truncate mt-0.5" style={{ color: '#7A828A' }}>{thread.lastMessage}</p>
                </div>
                {thread.nonLu > 0 && (
                  <span className="text-xs font-bold w-5 h-5 flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#C59B6C', color: '#0A192F' }}>
                    {thread.nonLu}
                  </span>
                )}
              </div>
            </button>
          ))
        )}
      </div>

      <div className={`flex-1 flex flex-col md:flex ${showThreads ? 'hidden' : 'flex'}`}>
        {activeThread ? (
          <>
            <div className="p-3 flex items-center gap-3" style={{ borderBottom: '1px solid #E5E5E0', backgroundColor: '#FAFAF6' }}>
              <button onClick={handleBack} className="md:hidden p-1 -ml-1" style={{ color: '#0A192F' }}>
                <FiArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F4F4F0' }}>
                <FiUser className="w-4 h-4" style={{ color: '#0A192F' }} />
              </div>
              <p className="text-sm font-bold truncate" style={{ color: '#0A192F' }}>{activeUserName}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
              {messages.map((msg) => {
                const isMe = msg.expediteurId === user.id;
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-[85%] md:max-w-lg p-3 md:p-4" style={{
                      backgroundColor: isMe ? '#F4F4F0' : '#FFFFFF',
                      border: '2px solid #0A192F',
                    }}>
                      <p className="text-sm" style={{ color: '#0A192F' }}>{msg.contenu}</p>
                      <p className="text-xs mt-1" style={{ color: '#7A828A' }}>
                        {new Date(msg.dateEnvoi).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <form onSubmit={handleSend} className="flex gap-2 md:gap-4 p-3 md:p-4" style={{ borderTop: '1px solid #E5E5E0' }}>
              <input className="input-root flex-1 min-w-0" placeholder="Écrivez votre message..."
                value={newMessage} onChange={(e) => setNewMessage(e.target.value)} />
              <button type="submit" className="btn-primary-root px-4 md:px-6">
                <FiSend className="w-5 h-5" />
              </button>
            </form>
          </>
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center" style={{ color: '#7A828A' }}>
            <div className="text-center">
              <FiMessageSquare className="w-16 h-16 mx-auto mb-4" style={{ opacity: 0.5 }} />
              <p>Sélectionnez une conversation</p>
            </div>
          </div>
        )}
      </div>

      {showNewMessage && (
        <NewMessageModal
          currentUserId={user.id}
          currentUserRole={user.role}
          onClose={() => setShowNewMessage(false)}
          onSelect={(otherUserId) => {
            setShowNewMessage(false);
            setActiveThread(otherUserId);
            fetchMessages(otherUserId);
            setShowThreads(false);
          }}
        />
      )}
    </div>
  );
}

const allowedRecipients = {
  student: ['admin', 'service_acad'],
  owner: ['student', 'admin', 'service_acad'],
  admin: ['student', 'owner', 'admin', 'service_acad'],
  service_acad: ['student', 'admin'],
};

const roleLabels = {
  student: 'Étudiant', owner: 'Propriétaire',
  admin: 'Administrateur', service_acad: 'Service Ac.',
};

function NewMessageModal({ currentUserId, currentUserRole, onClose, onSelect }) {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async (query) => {
    setLoading(true);
    try {
      const params = { limit: 20 };
      if (query.trim()) params.search = query;
      const res = await userAPI.getAll(params);
      const allowed = allowedRecipients[currentUserRole] || [];
      setUsers((res.data.users || []).filter(u => u.id !== currentUserId && allowed.includes(u.role)));
    } catch { setUsers([]); }
    finally { setLoading(false); }
  }, [currentUserId, currentUserRole]);

  useEffect(() => {
    fetchUsers(search);
  }, [fetchUsers]);

  useEffect(() => {
    const timer = setTimeout(() => fetchUsers(search), 300);
    return () => clearTimeout(timer);
  }, [search, fetchUsers]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}
      style={{ animation: 'fadeIn 0.15s ease' }}>
      <div className="bg-white w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}
        style={{ border: '2px solid #0A192F', animation: 'slideUp 0.2s ease' }}>
        <div className="p-6" style={{ borderBottom: '2px solid #E5E5E0' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#F4F4F0' }}>
                <FiMessageSquare className="w-5 h-5" style={{ color: '#0A192F' }} />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg" style={{ color: '#0A192F' }}>Nouveau message</h3>
                <p className="text-xs" style={{ color: '#7A828A' }}>Recherchez un utilisateur pour lui envoyer un message</p>
              </div>
            </div>
            <button className="text-2xl" style={{ color: '#7A828A' }} onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="p-6">
          <div className="relative mb-4">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#7A828A' }} />
            <input className="input-root pl-10 w-full" placeholder="Rechercher par nom ou email..."
              value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          </div>

          <div className="max-h-72 overflow-y-auto space-y-1">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent' }} />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8" style={{ color: '#7A828A' }}>
                <FiUser className="w-8 h-8 mx-auto mb-2" style={{ opacity: 0.5 }} />
                <p className="text-sm">Aucun utilisateur trouvé</p>
              </div>
            ) : (
              users.map((u) => (
                <button key={u.id} onClick={() => onSelect(u.id)}
                  className="w-full flex items-center gap-3 p-3 transition-colors hover:bg-parchment text-left"
                  style={{ border: '2px solid transparent', borderBottomColor: '#F0F0EC' }}>
                  {u.avatarUrl ? (
                    <img src={u.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F4F4F0' }}>
                      <FiUser className="w-5 h-5" style={{ color: '#0A192F' }} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: '#0A192F' }}>{u.nom}</p>
                    <p className="text-xs truncate" style={{ color: '#7A828A' }}>{u.email}</p>
                  </div>
                  <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 flex-shrink-0"
                    style={{ backgroundColor: '#F4F4F0', color: '#0A192F' }}>
                    {roleLabels[u.role] || u.role}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
